import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type TeamRole = 'owner' | 'admin' | 'manager' | 'viewer';

export interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: TeamRole;
  status: 'active' | 'pending' | 'inactive';
  invitedAt: Date;
  joinedAt: Date | null;
  lastActive: Date | null;
  avatar?: string;
}

export interface TeamInvite {
  id: string;
  email: string;
  role: TeamRole;
  invitedBy: string;
  invitedAt: Date;
  expiresAt: Date;
  token: string;
}

interface TeamContextType {
  teamMembers: TeamMember[];
  pendingInvites: TeamInvite[];
  currentUserRole: TeamRole;
  maxMembers: number;
  organizationId: string | null;
  isOwner: boolean;
  isAdmin: boolean;
  addMember: (email: string, name: string, role: TeamRole) => void;
  updateMemberRole: (id: string, role: TeamRole) => void;
  removeMember: (id: string) => void;
  inviteMember: (email: string, role: TeamRole) => TeamInvite;
  acceptInvite: (token: string, userName: string) => boolean;
  getInviteLink: (inviteId: string) => string | null;
  cancelInvite: (id: string) => void;
  resendInvite: (id: string) => void;
  canManageTeam: boolean;
  canInviteMembers: boolean;
  getRolePermissions: (role: TeamRole) => RolePermissions;
}

interface RolePermissions {
  canViewDashboard: boolean;
  canUploadReports: boolean;
  canExportReports: boolean;
  canManageSettings: boolean;
  canManageTeam: boolean;
  canManageBilling: boolean;
}

const ROLE_PERMISSIONS: Record<TeamRole, RolePermissions> = {
  owner: {
    canViewDashboard: true,
    canUploadReports: true,
    canExportReports: true,
    canManageSettings: true,
    canManageTeam: true,
    canManageBilling: true,
  },
  admin: {
    canViewDashboard: true,
    canUploadReports: true,
    canExportReports: true,
    canManageSettings: true,
    canManageTeam: true,
    canManageBilling: false, // Only owner can manage billing
  },
  manager: {
    canViewDashboard: true,
    canUploadReports: true,
    canExportReports: true,
    canManageSettings: true,
    canManageTeam: false,
    canManageBilling: false,
  },
  viewer: {
    canViewDashboard: true,
    canUploadReports: false,
    canExportReports: false,
    canManageSettings: false,
    canManageTeam: false,
    canManageBilling: false,
  },
};

const TeamContext = createContext<TeamContextType | undefined>(undefined);

const TEAM_STORAGE_KEY = 'ltss_team_members';
const INVITES_STORAGE_KEY = 'ltss_team_invites';
const MAX_TEAM_MEMBERS = 10;

// Generate a random invite token
function generateToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

export function TeamProvider({ children }: { children: ReactNode }) {
  const { user, subscription } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [pendingInvites, setPendingInvites] = useState<TeamInvite[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<TeamRole>('viewer');
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  // Determine current user's role based on subscription and team membership
  useEffect(() => {
    if (!user) {
      setCurrentUserRole('viewer');
      return;
    }

    const userEmail = user.email?.toLowerCase();
    
    // Check if user is the subscription owner (purchased the plan)
    // The person who purchased Enterprise becomes the owner
    if (subscription?.plan === 'enterprise' || subscription?.plan === 'pro') {
      // Check if this user is in the team as a member
      const memberRecord = teamMembers.find(m => m.email.toLowerCase() === userEmail);
      
      if (memberRecord) {
        setCurrentUserRole(memberRecord.role);
      } else {
        // If they have a subscription but aren't in team, they're the owner
        setCurrentUserRole('owner');
        // Create organization ID based on user
        setOrganizationId(user.id || userEmail || 'default-org');
      }
    } else {
      // Free plan users are viewers
      setCurrentUserRole('viewer');
    }
  }, [user, subscription, teamMembers]);

  // Load team data from storage
  useEffect(() => {
    const savedMembers = localStorage.getItem(TEAM_STORAGE_KEY);
    const savedInvites = localStorage.getItem(INVITES_STORAGE_KEY);
    
    if (savedMembers) {
      try {
        const parsed = JSON.parse(savedMembers);
        setTeamMembers(parsed.map((m: any) => ({
          ...m,
          invitedAt: new Date(m.invitedAt),
          joinedAt: m.joinedAt ? new Date(m.joinedAt) : null,
          lastActive: m.lastActive ? new Date(m.lastActive) : null,
        })));
      } catch (e) {
        console.error('Failed to load team members:', e);
      }
    }
    
    if (savedInvites) {
      try {
        const parsed = JSON.parse(savedInvites);
        setPendingInvites(parsed.map((i: any) => ({
          ...i,
          invitedAt: new Date(i.invitedAt),
          expiresAt: new Date(i.expiresAt),
        })));
      } catch (e) {
        console.error('Failed to load invites:', e);
      }
    }
  }, []);

  // Save team members
  const saveMembers = (members: TeamMember[]) => {
    localStorage.setItem(TEAM_STORAGE_KEY, JSON.stringify(members));
    setTeamMembers(members);
  };

  // Save invites
  const saveInvites = (invites: TeamInvite[]) => {
    localStorage.setItem(INVITES_STORAGE_KEY, JSON.stringify(invites));
    setPendingInvites(invites);
  };

  const addMember = (email: string, name: string, role: TeamRole) => {
    if (teamMembers.length >= MAX_TEAM_MEMBERS) {
      throw new Error('Team member limit reached');
    }
    
    const newMember: TeamMember = {
      id: Date.now().toString(),
      email,
      name,
      role,
      status: 'active',
      invitedAt: new Date(),
      joinedAt: new Date(),
      lastActive: new Date(),
    };
    
    saveMembers([...teamMembers, newMember]);
  };

  const updateMemberRole = (id: string, role: TeamRole) => {
    saveMembers(teamMembers.map(m => 
      m.id === id ? { ...m, role } : m
    ));
  };

  const removeMember = (id: string) => {
    saveMembers(teamMembers.filter(m => m.id !== id));
  };

  const inviteMember = (email: string, role: TeamRole): TeamInvite => {
    const invite: TeamInvite = {
      id: Date.now().toString(),
      email,
      role,
      invitedBy: user?.email || 'unknown',
      invitedAt: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days
      token: generateToken(),
    };
    
    saveInvites([...pendingInvites, invite]);
    
    // Generate invite link
    const inviteLink = `${window.location.origin}/invite?token=${invite.token}`;
    console.log('Invite link generated:', inviteLink);
    
    return invite;
  };
  
  // Accept an invite (called when user clicks invite link)
  const acceptInvite = (token: string, userName: string): boolean => {
    const invite = pendingInvites.find(i => i.token === token);
    if (!invite) return false;
    
    // Check if invite is expired
    if (new Date() > new Date(invite.expiresAt)) {
      return false;
    }
    
    // Add user as team member
    const newMember: TeamMember = {
      id: Date.now().toString(),
      email: invite.email,
      name: userName,
      role: invite.role,
      status: 'active',
      invitedAt: invite.invitedAt,
      joinedAt: new Date(),
      lastActive: new Date(),
    };
    
    saveMembers([...teamMembers, newMember]);
    
    // Remove the invite
    saveInvites(pendingInvites.filter(i => i.token !== token));
    
    return true;
  };
  
  // Get invite link for sharing
  const getInviteLink = (inviteId: string): string | null => {
    const invite = pendingInvites.find(i => i.id === inviteId);
    if (!invite) return null;
    return `${window.location.origin}/invite?token=${invite.token}`;
  };

  const cancelInvite = (id: string) => {
    saveInvites(pendingInvites.filter(i => i.id !== id));
  };

  const resendInvite = (id: string) => {
    saveInvites(pendingInvites.map(i => 
      i.id === id 
        ? { ...i, invitedAt: new Date(), expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
        : i
    ));
  };

  const getRolePermissions = (role: TeamRole): RolePermissions => {
    return ROLE_PERMISSIONS[role];
  };

  const canManageTeam = ROLE_PERMISSIONS[currentUserRole].canManageTeam;
  const canInviteMembers = canManageTeam && (teamMembers.length + pendingInvites.length) < MAX_TEAM_MEMBERS;
  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <TeamContext.Provider value={{
      teamMembers,
      pendingInvites,
      currentUserRole,
      maxMembers: MAX_TEAM_MEMBERS,
      organizationId,
      isOwner,
      isAdmin,
      addMember,
      updateMemberRole,
      removeMember,
      inviteMember,
      acceptInvite,
      getInviteLink,
      cancelInvite,
      resendInvite,
      canManageTeam,
      canInviteMembers,
      getRolePermissions,
    }}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}
